import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Image from '@tiptap/extension-image';
import { useEffect, useRef, useState } from 'react';
import {
  Bold,
  Italic,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  Undo,
  Redo,
  Link as LinkIcon,
  Code,
  Minus,
  Eraser,
  ImagePlus,
  Loader2,
  AlignLeft,
  AlignRight,
  AlignCenter,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { ApiError } from '@/lib/api-client';

export function RichEditor({
  value,
  onChange,
  placeholder = 'İçeriği buraya yaz veya yapıştır…',
  minHeight = 400,
  contentId,
}: {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: number;
  /** If provided, uploaded images get associated with this content and
   *  appear in its gallery + WP dispatch media pipeline. */
  contentId?: string;
}) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const editor = useEditor({
    extensions: [
      // StarterKit v3+ ships its own Link extension. We disable it here
      // and register our own configured instance below so external links
      // get rel=noopener + primary styling.
      StarterKit.configure({
        heading: { levels: [2, 3, 4] },
        codeBlock: { HTMLAttributes: { class: 'rounded bg-muted p-3 text-xs font-mono' } },
        link: false,
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { rel: 'noopener noreferrer', class: 'text-primary underline' },
      }),
      Placeholder.configure({ placeholder }),
      Image.configure({
        inline: false,
        allowBase64: false,
        HTMLAttributes: {
          class: 'rounded-md my-3 max-w-full h-auto',
          loading: 'lazy',
        },
      }),
    ],
    content: value || '',
    onUpdate: ({ editor: e }) => onChange(e.getHTML()),
    editorProps: {
      attributes: {
        class: cn(
          'prose prose-sm max-w-none dark:prose-invert focus:outline-none',
          '[&_h2]:mt-6 [&_h2]:mb-2 [&_h3]:mt-4 [&_h3]:mb-1',
          '[&_p]:my-2 [&_ul]:my-2 [&_ol]:my-2 [&_blockquote]:border-l-2 [&_blockquote]:pl-3',
          'px-3 py-2 rounded-b-md border-t-0 border border-input',
        ),
        style: `min-height: ${minHeight}px`,
      },
    },
  });

  // Sync external value → editor (e.g. after AI generate)
  useEffect(() => {
    if (!editor) return;
    if (editor.getHTML() !== value) {
      editor.commands.setContent(value || '', { emitUpdate: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  if (!editor) return null;

  const promptLink = () => {
    const current = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('Link URL (kaldırmak için boş bırak):', current ?? '');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    } else {
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }
  };

  const onImagePick = () => {
    setUploadError(null);
    fileInputRef.current?.click();
  };

  const onImageChosen = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    setUploadError(null);
    try {
      const body = new FormData();
      body.append('file', file);
      if (contentId) body.append('content_id', contentId);
      body.append('alt_text', file.name.replace(/\.[^.]+$/, '').slice(0, 120));
      const res = await fetch('/api/app/images/upload', {
        method: 'POST',
        credentials: 'include',
        body,
      });
      const json = (await res.json()) as
        | { ok: true; data: { public_url: string; alt_text: string | null } }
        | { ok: false; error: { code: string; message: string } };
      if (!json.ok) throw new ApiError(json.error.message, json.error.code, res.status);
      editor
        .chain()
        .focus()
        .setImage({ src: json.data.public_url, alt: json.data.alt_text ?? '' })
        .run();
    } catch (err) {
      setUploadError(err instanceof ApiError ? err.message : 'Yüklenemedi');
    } finally {
      setUploading(false);
    }
  };

  /** Set data-align on the currently-selected image so our CSS (or WP's)
   *  floats it left/right or centers it. WordPress + CSS: class-based. */
  const alignImage = (align: 'left' | 'right' | 'center') => {
    if (!editor.isActive('image')) return;
    const wrapperClass =
      align === 'center'
        ? 'wp-image aligncenter rounded-md my-3 max-w-full h-auto mx-auto block'
        : align === 'left'
          ? 'wp-image alignleft rounded-md max-w-[45%] mr-4 mb-2 float-left'
          : 'wp-image alignright rounded-md max-w-[45%] ml-4 mb-2 float-right';
    editor.chain().focus().updateAttributes('image', { class: wrapperClass }).run();
  };

  return (
    <div className="rounded-md border border-input bg-background overflow-hidden">
      <div className="flex flex-wrap items-center gap-0.5 border-b bg-muted/30 p-1">
        <ToolbarBtn
          active={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
          title="Kalın (Ctrl+B)"
        >
          <Bold className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn
          active={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          title="İtalik (Ctrl+I)"
        >
          <Italic className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn
          active={editor.isActive('code')}
          onClick={() => editor.chain().focus().toggleCode().run()}
          title="Kod (inline)"
        >
          <Code className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <Separator />
        <ToolbarBtn
          active={editor.isActive('heading', { level: 2 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          title="H2 başlık"
        >
          <Heading2 className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn
          active={editor.isActive('heading', { level: 3 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          title="H3 alt başlık"
        >
          <Heading3 className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <Separator />
        <ToolbarBtn
          active={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          title="Madde işaretli liste"
        >
          <List className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn
          active={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          title="Numaralı liste"
        >
          <ListOrdered className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn
          active={editor.isActive('blockquote')}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          title="Alıntı"
        >
          <Quote className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <Separator />
        <ToolbarBtn
          active={editor.isActive('link')}
          onClick={promptLink}
          title="Link"
        >
          <LinkIcon className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <Separator />
        <ToolbarBtn
          onClick={onImagePick}
          title="Görsel ekle (R2'ye yükle)"
          disabled={uploading}
        >
          {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5" />}
        </ToolbarBtn>
        {editor.isActive('image') && (
          <>
            <ToolbarBtn onClick={() => alignImage('left')} title="Görseli sola yaslat (yazı sağa akar)">
              <AlignLeft className="w-3.5 h-3.5" />
            </ToolbarBtn>
            <ToolbarBtn onClick={() => alignImage('center')} title="Görseli ortala (tam satır)">
              <AlignCenter className="w-3.5 h-3.5" />
            </ToolbarBtn>
            <ToolbarBtn onClick={() => alignImage('right')} title="Görseli sağa yaslat (yazı sola akar)">
              <AlignRight className="w-3.5 h-3.5" />
            </ToolbarBtn>
          </>
        )}
        <Separator />
        <ToolbarBtn
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          title="Yatay çizgi"
        >
          <Minus className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn
          onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
          title="Formatı temizle"
        >
          <Eraser className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <Separator />
        <ToolbarBtn
          onClick={() => editor.chain().focus().undo().run()}
          title="Geri al"
          disabled={!editor.can().undo()}
        >
          <Undo className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn
          onClick={() => editor.chain().focus().redo().run()}
          title="Yeniden yap"
          disabled={!editor.can().redo()}
        >
          <Redo className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <div className="flex-1" />
        <span className="text-[10px] text-muted-foreground px-2">
          {editor.storage.characterCount?.characters?.() ?? ''}
        </span>
      </div>
      {uploadError && (
        <div className="text-xs text-destructive bg-destructive/10 px-3 py-1.5 border-b">
          {uploadError}
        </div>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        className="hidden"
        onChange={onImageChosen}
      />
      <EditorContent editor={editor} />
    </div>
  );
}

function ToolbarBtn({
  children,
  active,
  ...props
}: {
  children: React.ReactNode;
  active?: boolean;
} & React.ComponentProps<typeof Button>) {
  return (
    <Button
      type="button"
      size="sm"
      variant={active ? 'secondary' : 'ghost'}
      className="h-7 w-7 p-0"
      {...props}
    >
      {children}
    </Button>
  );
}

function Separator() {
  return <div className="h-5 w-px bg-border mx-0.5" />;
}
